import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const PLANS = [
    {
        id: 'starter',
        name: 'Starter Pack',
        price: 9,
        credits: 200,
        features: ['200 Credits', '~ $0.045 / hit cost', 'No Expiry', 'Email Support', 'Basic Rate Limits'],
        cta: 'Get Started',
        href: '/dashboard?plan=starter',
        popular: false
    },
    {
        id: 'growth',
        name: 'Growth Pack',
        price: 19,
        credits: 450,
        features: ['450 Credits', '~ $0.042 / hit cost', 'No Expiry', 'Email Support', 'Higher Rate Limits'],
        cta: 'Get Professional',
        href: '/dashboard?plan=growth',
        popular: false
    },
    {
        id: 'enterprise',
        name: 'Lifetime Membership',
        price: 99,
        credits: 'Membership + PAYG',
        features: ['WhatsApp & Email Support', 'Integration Assistance', 'Team Support', 'Lifetime Membership', 'Advanced API Analytics'],
        cta: 'Get Access',
        href: '/dashboard?plan=enterprise',
        popular: true
    }
];

export function Pricing() {
    return (
        <div className="bg-background py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-base font-semibold leading-7 text-primary">Pricing</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                        Simple, Transparent Pricing
                    </p>
                    <p className="mt-6 text-lg leading-8 text-muted-foreground">
                        Choose the perfect plan for your needs. Whether you're a casual user or a professional, we have you covered.
                    </p>
                </div>
                <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 gap-x-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.id}
                            className={`flex flex-col justify-between rounded-3xl bg-card p-8 ring-1 xl:p-10 ${plan.popular
                                ? 'ring-primary shadow-xl scale-105 relative z-10'
                                : 'ring-border hover:ring-primary/50 transition-all'
                                }`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-sm font-semibold text-primary-foreground shadow-sm">
                                    Most Popular
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between gap-x-4">
                                    <h3 className={`text-lg font-semibold leading-8 ${plan.popular ? 'text-primary' : 'text-foreground'}`}>
                                        {plan.name}
                                    </h3>
                                </div>
                                <p className="mt-6 flex items-baseline gap-x-1">
                                    <span className="text-4xl font-bold tracking-tight text-foreground">${plan.price}</span>
                                </p>
                                <p className="mt-2 text-sm text-primary font-medium">
                                    {typeof plan.credits === 'string' ? plan.credits : `${plan.credits} Credits`}
                                </p>
                                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                                    {plan.features.map((feature) => (
                                        <li key={feature} className="flex gap-x-3">
                                            <Check className={`h-6 w-5 flex-none ${plan.popular ? 'text-primary' : 'text-primary'}`} aria-hidden="true" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <Link
                                to={plan.href}
                                className={`mt-8 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${plan.popular
                                    ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline-primary'
                                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 ring-1 ring-inset ring-border'
                                    }`}
                            >
                                {plan.cta}
                            </Link>
                        </div>
                    ))}
                </div>

                {/* Lifetime Membership Benefits Detail */}
                <div className="mt-24 rounded-2xl bg-muted/30 p-8 ring-1 ring-border sm:p-10">
                    <div className="mx-auto max-w-3xl text-center">
                        <h3 className="text-2xl font-bold tracking-tight text-foreground">Understanding Lifetime Membership</h3>
                        <p className="mt-6 text-lg leading-8 text-muted-foreground">
                            Our <span className="text-primary font-semibold">Lifetime Membership ($99)</span> is designed for power users and businesses who want the absolute lowest rates without recurring overhead.
                        </p>
                    </div>
                    <dl className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2">
                        <div className="flex flex-col gap-y-3 border-l border-primary/20 pl-6">
                            <dt className="text-sm font-semibold leading-6 text-muted-foreground">One-Time Activation</dt>
                            <dd className="order-first text-3xl font-semibold tracking-tight text-foreground">$99</dd>
                            <dd className="text-xs text-muted-foreground">Pay once, unlock the benefits forever. No monthly subscriptions.</dd>
                        </div>
                        <div className="flex flex-col gap-y-3 border-l border-primary/20 pl-6">
                            <dt className="text-sm font-semibold leading-6 text-muted-foreground">Lowest Hit Rate</dt>
                            <dd className="order-first text-3xl font-semibold tracking-tight text-foreground">$0.02</dd>
                            <dd className="text-xs text-muted-foreground">The most competitive rate in the market. Only pay for what you use.</dd>
                        </div>
                        <div className="flex flex-col gap-y-3 border-l border-primary/20 pl-6">
                            <dt className="text-sm font-semibold leading-6 text-muted-foreground">Post-Paid Usage</dt>
                            <dd className="order-first text-3xl font-semibold tracking-tight text-foreground">Usage Bill</dd>
                            <dd className="text-xs text-muted-foreground">You are billed only based on your usage, and you can pay that bill whenever you choose.</dd>
                        </div>
                        <div className="flex flex-col gap-y-3 border-l border-primary/20 pl-6">
                            <dt className="text-sm font-semibold leading-6 text-muted-foreground">Premium Support</dt>
                            <dd className="order-first text-3xl font-semibold tracking-tight text-foreground">Team Help</dd>
                            <dd className="text-xs text-muted-foreground">WhatsApp support, email help, and custom integration assistance for your team.</dd>
                        </div>
                    </dl>
                    <div className="mt-10 flex items-center justify-center gap-x-6">
                        <p className="text-center text-sm text-muted-foreground italic">
                            * Starter and Growth packs have fixed credits and slightly higher per-hit rates ($0.045 and $0.042).
                            The Lifetime plan is the best choice for long-term scalability.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
